/**
 * The Bot models admin page's working state (components/AppAdmin.tsx), kept
 * apart from React so it can be tested on its own.
 *
 * The app Worker answers GET /api/admin/models with every priced model and the
 * current offer. The page shows one row per model: the offered ones first, in
 * the order users see them, the first of them the default, then any the
 * provider stopped pricing, then the rest by name. Edits stay on the page until
 * Save, which replaces the whole offer in the order shown.
 */

/** A priced model, in credits per million tokens (the price list's own columns). */
export interface AdminPrice {
  readonly model: string;
  readonly input_per_million: number;
  readonly output_per_million: number;
  readonly cache_write_per_million: number | null;
  readonly cache_read_per_million: number | null;
  /** Vision: whether the model takes images, 1 or 0, from the provider's list; null until the list has said. */
  readonly vision?: number | null;
}

/** A model on offer, as saved. */
export interface AdminOffer {
  readonly model: string;
  readonly label: string;
  readonly position: number;
}

export interface AdminModelsAnswer {
  readonly prices: readonly AdminPrice[];
  readonly offered: readonly AdminOffer[];
}

/** What a request is charged, per million tokens: fresh input at the cache-write rate where there is one, cached input at the read rate. */
export interface ChargedRates {
  readonly input: number;
  readonly cached: number;
  readonly output: number;
}

export interface ModelRow {
  readonly model: string;
  /** The name users see. Kept as typed, so a half-edited name is not trimmed under the cursor. */
  readonly label: string;
  readonly offered: boolean;
  /** On offer, but the provider no longer prices it: shown unticked, and dropped for good at the next save. */
  readonly withdrawn: boolean;
  /** Null for a withdrawn model, which has no price left to show. */
  readonly rates: ChargedRates | null;
  /** Vision: true or false; null for a withdrawn model, or one the provider's list has not said. */
  readonly vision: boolean | null;
}

export interface SavedModel {
  readonly model: string;
  readonly label: string;
}

export const MAX_OFFERED = 50;

export function visionOf(price: AdminPrice | undefined): boolean | null {
  return price?.vision == null ? null : price.vision === 1;
}

export function chargedRates(price: AdminPrice): ChargedRates {
  return {
    input: price.cache_write_per_million ?? price.input_per_million,
    cached: price.cache_read_per_million ?? price.input_per_million,
    output: price.output_per_million,
  };
}

/** Offered first in their order, then withdrawn, then the rest by model ID. */
export function normalize(rows: readonly ModelRow[]): ModelRow[] {
  const byModel = (a: ModelRow, b: ModelRow): number => (a.model < b.model ? -1 : a.model > b.model ? 1 : 0);
  return [
    ...rows.filter((row) => row.offered),
    ...rows.filter((row) => !row.offered && row.withdrawn),
    ...rows.filter((row) => !row.offered && !row.withdrawn).sort(byModel),
  ];
}

export function modelRows(answer: AdminModelsAnswer): ModelRow[] {
  const priced = new Map(answer.prices.map((price) => [price.model, price]));
  const offered = [...answer.offered].sort((a, b) => a.position - b.position);
  const onOffer = new Set(offered.map((offer) => offer.model));
  const rows: ModelRow[] = offered.map((offer) => {
    const price = priced.get(offer.model);
    return { model: offer.model, label: offer.label, offered: price != null, withdrawn: price == null, rates: price == null ? null : chargedRates(price), vision: visionOf(price) };
  });
  for (const price of answer.prices) {
    if (!onOffer.has(price.model)) rows.push({ model: price.model, label: "", offered: false, withdrawn: false, rates: chargedRates(price), vision: visionOf(price) });
  }
  return normalize(rows);
}

/** The default: the first model on offer, what a bot runs until its user picks one; null while nothing is. */
export function defaultRow(rows: readonly ModelRow[]): ModelRow | null {
  return rows.find((row) => row.offered) ?? null;
}

/** Ticking a model with no name names it after its ID, and puts it at the end of the offer. */
export function setOffered(rows: readonly ModelRow[], model: string, offered: boolean): ModelRow[] {
  const row = rows.find((candidate) => candidate.model === model);
  if (row == null || row.withdrawn || row.offered === offered) return [...rows];
  const changed: ModelRow = { ...row, offered, label: offered && row.label.trim() === "" ? row.model : row.label };
  return normalize([...rows.filter((candidate) => candidate.model !== model), changed]);
}

export function setLabel(rows: readonly ModelRow[], model: string, label: string): ModelRow[] {
  return rows.map((row) => (row.model === model ? { ...row, label } : row));
}

/** Move an offered model one place up (-1) or down (+1) within the offer. */
export function move(rows: readonly ModelRow[], model: string, by: -1 | 1): ModelRow[] {
  const offered = rows.filter((row) => row.offered);
  const from = offered.findIndex((row) => row.model === model);
  const to = from + by;
  if (from < 0 || to < 0 || to >= offered.length) return [...rows];
  const moved = [...offered];
  const [row] = moved.splice(from, 1);
  moved.splice(to, 0, row!);
  return [...moved, ...rows.filter((candidate) => !candidate.offered)];
}

/** The search narrows the rest; what is on offer, or has just left it, always shows. */
export function visibleRows(rows: readonly ModelRow[], query: string): ModelRow[] {
  const wanted = query.trim().toLowerCase();
  return rows.filter((row) => row.offered || row.withdrawn || wanted === "" || row.model.toLowerCase().includes(wanted) || row.label.toLowerCase().includes(wanted));
}

export function displayName(row: Pick<ModelRow, "model" | "label">): string {
  return row.label.trim() || row.model;
}

/** What Save sends: the whole offer, in order; the first is the default. */
export function savePayload(rows: readonly ModelRow[]): { models: SavedModel[] } {
  return { models: rows.filter((row) => row.offered).map((row) => ({ model: row.model, label: displayName(row) })) };
}

/** Whether the page differs from what was loaded. A withdrawn model dropping out at the next save counts as a change. */
export function hasChanges(answer: AdminModelsAnswer, rows: readonly ModelRow[]): boolean {
  const loaded = savePayload(modelRows(answer));
  const stale = rows.some((row) => row.withdrawn);
  return stale || JSON.stringify(loaded) !== JSON.stringify(savePayload(rows));
}

/** Why Save must wait, or null when the offer can go as it is. */
export function problem(rows: readonly ModelRow[]): string | null {
  const offered = rows.filter((row) => row.offered);
  if (offered.length > MAX_OFFERED) return `Offer at most ${MAX_OFFERED} models.`;
  const long = offered.find((row) => displayName(row).length > 60);
  return long == null ? null : `The name for ${long.model} is longer than 60 characters.`;
}

const credits = (value: number): string => value.toLocaleString("en-US", { maximumFractionDigits: 1 });

export function ratesText(row: Pick<ModelRow, "rates">): string {
  return row.rates == null ? "—" : `${credits(row.rates.input)} in · ${credits(row.rates.cached)} cached · ${credits(row.rates.output)} out`;
}

export function visionText(row: Pick<ModelRow, "vision">): string {
  return row.vision == null ? "—" : String(row.vision);
}

/** What the page says once the offer is saved. */
export function savedMessage(rows: readonly ModelRow[]): string {
  const offered = rows.filter((row) => row.offered);
  if (offered.length === 0) return "Nothing offered: bots have no model to run until one is.";
  return `Offering ${offered.length} model${offered.length === 1 ? "" : "s"}; the default is ${displayName(offered[0]!)}.`;
}
